import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { siteApi, type FieldValue, type SiteTheme, type SiteStatus, type ServiceInput } from './api.ts';

export const siteKey = ['site'] as const;

// The whole site bundle. Every page reads from this one query so a mutation on
// any page (section save, theme, optional toggle, document) refreshes the rest.
export function useSiteQuery() {
  return useQuery({ queryKey: siteKey, queryFn: siteApi.get, retry: false });
}

export function useSaveSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { group: string; fields: Record<string, FieldValue> }) =>
      siteApi.putSection(v.group, v.fields),
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

export function useSaveTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { templateId?: string; theme?: SiteTheme; status?: SiteStatus; customDomain?: string | null }) =>
      siteApi.patchTheme(patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

// Publish / unpublish — flips Site.status via the same theme patch endpoint.
export function useSetStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: SiteStatus) => siteApi.patchTheme({ status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

export function useSetOptional() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sectionId: string; enabled: boolean }) => siteApi.setOptional(v.sectionId, v.enabled),
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: siteApi.uploadDocument,
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => siteApi.deleteDocument(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

export function useCreateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ServiceInput) => siteApi.createService(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

export function useUpdateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; input: ServiceInput }) => siteApi.updateService(v.id, v.input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

export function useDeleteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => siteApi.deleteService(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

export function useReorderServices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => siteApi.reorderServices(ids),
    onSuccess: () => void qc.invalidateQueries({ queryKey: siteKey }),
  });
}

export const submissionsKey = (serviceId: string) => ['submissions', serviceId] as const;

export function useServiceSubmissions(serviceId: string, enabled: boolean) {
  return useQuery({ queryKey: submissionsKey(serviceId), queryFn: () => siteApi.listSubmissions(serviceId), enabled, retry: false });
}

export function useSetSubmissionStatus(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: 'new' | 'read' }) => siteApi.setSubmissionStatus(v.id, v.status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsKey(serviceId) });
      void qc.invalidateQueries({ queryKey: siteKey });
    },
  });
}

export function useDeleteSubmission(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => siteApi.deleteSubmission(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsKey(serviceId) });
      void qc.invalidateQueries({ queryKey: siteKey });
    },
  });
}
